import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FacilityConfig } from 'src/environments/environment.interface';
import { DomSanitizer, SafeUrl, SafeHtml } from '@angular/platform-browser';
import { Address, InterestedPerson, Location, PersonModel, SiteMeta } from 'src/app/models/common-models';
import { InterestedPersonService } from 'src/app/services/interested-person.service';
import { AlertService } from 'src/app/services/alert.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { Utils } from 'src/app/services/common/utils';
import { FacilityService } from 'src/app/services/facility.service';
import { MemberService } from 'src/app/services/member.service';
import { UserService } from 'src/app/services/user.service';
import { MemberModel, MemberProfileSaveRequest, Registration } from 'src/app/models/member';
import { MemberLocationSaveRequest } from 'src/app/models/requests';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-get-started',
  templateUrl: './get-started.component.html',
  styleUrls: ['./get-started.component.scss']
})
export class GetStartedComponent implements OnInit, OnDestroy {
  getStartedForm!: FormGroup;

  isSent: boolean = false;
  isDisabled: boolean = true;
  facilityConfig: FacilityConfig;

  // Custom validators
  static nameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const value = control.value.trim();

      // Check minimum length
      if (value.length < 2) {
        return { minlength: { requiredLength: 2, actualLength: value.length } };
      }

      // Check for legal name characters (letters, spaces, apostrophes, hyphens)
      const namePattern = /^[a-zA-Z\s\-']+$/;
      if (!namePattern.test(value)) {
        return { invalidCharacters: true };
      }

      return null;
    };
  }

  static phoneValidator(dialCodeControl: FormControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const phoneValue = control.value.replace(/\D/g, ''); // Remove non-digits
      const dialCode = dialCodeControl?.value || '+1';

      // Check if all characters are numbers
      if (control.value !== phoneValue) {
        return { nonNumeric: true };
      }

      // If dial code is +1, require exactly 10 digits
      if (dialCode === '+1') {
        if (phoneValue.length !== 10) {
          return { invalidLength: { requiredLength: 10, actualLength: phoneValue.length } };
        }
      }

      return null;
    };
  }

  submitted: boolean = false;
  privacyPolicyUrl: string = "";
  termsUrl: string = "";
  isSmsNotificationVisible: boolean = false;
  parsedDescription: SafeHtml; // SafeHtml for parsed description with links
  countryCode = 'us';
  flagUrl: string = `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/us.svg`;
  dialCode = "+1";
  public siteMeta : SiteMeta;
  isButtonDisabled: boolean = true;
  locations: Location[] = [];
  authStep: 'form' | 'verify' = 'form';
  codeValue = '';
  loginErrorMessage: string | null = null;
  isLoading = false;
  isNewMember = false;
  memberInfo!: MemberModel;
  registration!: Registration;
  private readonly _unsubscribe: Subject<any> = new Subject();

  private static readonly DEFAULT_PROSPECT_REQUEST: InterestedPerson = {
    LocationId: 0,
    FirstName: '',
    LastName: '',
    DialCode: '+1',
    PhoneNumber: '',
    Email: '',
    EmailNotification: true,
    SmsNotification: false,
    FullName: '',
    Gender: '',
    BirthDate: '',
    Interests: '',
    AddressLine1: '',
    AddressLine2: '',
    City: '',
    State: '',
    ZipCode: '',
    Country: '',
    MenuPackId: 0,
    PaymentMethod: '',
    PurchaseId: '',
    PurchasedAt: null,
    Amount: null,
    PurchaseDescription: '',
    CreatedAt: '',
    Guid: '',
    Id: 0
  };

  prospectRequest: InterestedPerson = { ...GetStartedComponent.DEFAULT_PROSPECT_REQUEST };
  
  constructor(
      private auth: AuthService
    , private fb: FormBuilder
    , private _environmentService: EnvironmentService
    , private sanitizer: DomSanitizer
    , private interestedPersonService: InterestedPersonService
    , private alertService: AlertService
    , private localStorageService: LocalStorageService
    , private facilityService: FacilityService
    , private memberService: MemberService
    , private userService: UserService
  ) {
    
  }

  async ngOnInit() {
    this.facilityConfig = this._environmentService.getFacilityConfig();
    this.privacyPolicyUrl = this._environmentService.getLinkUrlById('privacyPolicy');
    this.termsUrl = this._environmentService.getLinkUrlById('termsAndConditions')
      || this._environmentService.getLinkUrlById('termsOfUse');
    // Parse description to convert link markup to HTML
    this.parsedDescription = Utils.parseDescriptionWithLinks(
      this.facilityConfig.getStartedPage.description,
      this._environmentService,
      this.sanitizer
    );
    // Load locations
    this.locations = await this.facilityService.getLocations();
    
    // Check if temp location is stored in localStorage and pre-fill the form
    const storedTempLocation = this.localStorageService.getTempLocation();
    if (storedTempLocation && storedTempLocation.Id) {
      // Update prospectRequest with stored temp location ID
      this.prospectRequest = { ...this.prospectRequest, LocationId: storedTempLocation.Id };
    }
    
    this.initForm(this.prospectRequest);
    
    // Subscribe to LocationId changes to save location to localStorage
    this.getStartedForm.get('LocationId')?.valueChanges.pipe(
      takeUntil(this._unsubscribe)
    ).subscribe(async (locationId: number) => {
      if (locationId && locationId > 0) {
        try {
          const location: Location = await this.facilityService.getLocationById(locationId);
          this.localStorageService.setLocation(location);
        } catch (err) {
          console.error('Error fetching location:', err);
        }
      }
    });
  }

  initForm(prospectRequest?: InterestedPerson): void {
    const initial: InterestedPerson = prospectRequest || this.createDefaultInterestedPerson({
      PaymentMethod: 'us_bank_account',
      Amount: 0
    });
    this.prospectRequest = initial;
    
    this.getStartedForm = this.fb.group({
      LocationId: [initial.LocationId, [Validators.required]],
      FirstName: [initial.FirstName, [Validators.required, GetStartedComponent.nameValidator()]],
      LastName: [initial.LastName, [Validators.required, GetStartedComponent.nameValidator()]],
      DialCode: [initial.DialCode, [Validators.required]],
      PhoneNumber: [initial.PhoneNumber, [Validators.required]],
      Code: ['', []],
      Email: [initial.Email, [Validators.required, Validators.email]],
      PaymentMethod: [initial.PaymentMethod, []],
      EmailNotification: [initial.EmailNotification, []],
      SmsNotification: [initial.SmsNotification, []],
    });

    // Apply phone validator dynamically based on dial code
    const phoneControl = this.getStartedForm.get('PhoneNumber');
    const dialCodeControl = this.getStartedForm.get('DialCode');

    if (phoneControl && dialCodeControl) {
      phoneControl.setValidators([
        Validators.required,
        GetStartedComponent.phoneValidator(dialCodeControl as FormControl)
      ]);
      phoneControl.updateValueAndValidity();

      // Update phone validation when dial code changes
      dialCodeControl.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(() => {
        phoneControl.setValidators([
          Validators.required,
          GetStartedComponent.phoneValidator(dialCodeControl as FormControl)
        ]);
        phoneControl.updateValueAndValidity();
      });
    }

    // Detect when FirstName changes
    this.getStartedForm.get('FirstName')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when LastName changes
    this.getStartedForm.get('LastName')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when DialCode changes
    this.getStartedForm.get('DialCode')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when PhoneNumber changes
    this.getStartedForm.get('PhoneNumber')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
      if (this.authStep === 'verify') {
        this.resetVerification();
      }
      this.submitButtonToggle();
    });

    // Detect when Email changes
    this.getStartedForm.get('Email')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when LocationId changes
    this.getStartedForm.get('LocationId')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
      this.submitButtonToggle();
    });

    this.getStartedForm.get('Code')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
      this.codeChange(value || '');
      this.submitButtonToggle();
    });

    // Initial button state - disabled if LocationId is not set
    this.submitButtonToggle();
  }

  validateField(fieldName: string): void {
    const control = this.getStartedForm.get(fieldName);
    if (control) {
      control.markAsTouched();

      // Strip non-digit characters from phone number when it loses focus
      if (fieldName === 'PhoneNumber' && control.value) {
        const digitsOnly = control.value.replace(/\D/g, '');
        if (digitsOnly !== control.value) {
          control.setValue(digitsOnly, { emitEvent: false });
        }
      }

      control.updateValueAndValidity();
    }
  }

  getLinkUrlById(id: string): SafeUrl | string {
    var linkUrl = this._environmentService.getLinkUrlById(id);
    if(id === 'appStore' && linkUrl === '') {
      linkUrl = 'itms-apps://?action=discover&referrer=app-store/';
      return this.sanitizer.bypassSecurityTrustUrl(linkUrl);
    }
    else if(id === 'playStore' && linkUrl === '') {
      linkUrl = 'https://play.google.com/store/apps?hl=es_419';
    }
    return linkUrl;
  }

  async onSubmit() {
    this.loginErrorMessage = null;

    if (!this.shouldAuthenticateGetStarted()) {
      await this.submitLegacyGetStarted();
      return;
    }

    if (this.authStep === 'verify') {
      await this.verifyCode();
      return;
    }

    this.submitted = true;
    if (!this.areRequiredFieldsValid()) {
      this.markRequiredFieldsTouched();
      this.submitButtonToggle();
      this.submitted = false;
      return;
    }

    this.isLoading = true;
    this.isButtonDisabled = true;

    try {
      const request = this.buildInterestedPersonRequest();
      this.localStorageService.setProp('df-auth-key', 'DialCode', request.DialCode);
      this.localStorageService.setProp('df-auth-key', 'PhoneNumber', request.PhoneNumber);

      // This flow authenticates booking through the member account, not session prospect info.
      sessionStorage.removeItem('prospectInfo');

      const result = await firstValueFrom(
        this.interestedPersonService.saveInterestedPerson(request).pipe(takeUntil(this._unsubscribe))
      );
      this.prospectRequest = {
        ...GetStartedComponent.DEFAULT_PROSPECT_REQUEST,
        ...result
      };

      await this.startAuthentication();
    } catch (error) {
      this.loginErrorMessage = 'Failed to save information. Please try again.';
      this.alertService.error(this.loginErrorMessage, true, true);
    } finally {
      this.submitted = false;
      this.isLoading = false;
      this.submitButtonToggle();
    }
  }

  private async submitLegacyGetStarted(): Promise<void> {
    this.submitted = true;
    if (!this.getStartedForm.valid) {
      this.isButtonDisabled = true;
      this.submitted = false;
      return;
    }
    this.isButtonDisabled = true;

    if (!this.areRequiredFieldsValid()) {
      this.submitted = false;
      this.isButtonDisabled = false;
      return;
    }

    this.localStorageService.setProp('df-auth-key', 'DialCode', this.getStartedForm.get('DialCode')?.value);
    this.localStorageService.setProp('df-auth-key', 'PhoneNumber', this.getStartedForm.get('PhoneNumber')?.value);

    const request = this.buildInterestedPersonRequest();

    // Preserve the existing non-Steelhaus prospect handoff behavior.
    sessionStorage.setItem('prospectInfo', JSON.stringify(request));

    this.interestedPersonService.saveInterestedPerson(request).pipe(
      takeUntil(this._unsubscribe)
    ).subscribe({
      next: (result: InterestedPerson) => {
        sessionStorage.setItem('prospectInfo', JSON.stringify(result));
        this.submitted = false;
        this.isButtonDisabled = false;
        this.alertService.success('Information saved successfully.', true, true);
        window.location.href = this.facilityConfig.getStartedPage.onSubmitRedirectTo || '/get-started';
      },
      error: () => {
        this.submitted = false;
        this.isButtonDisabled = false;
        this.alertService.error('Failed to save information.', true, true);
      }
    });
  }

  async resendCode(): Promise<void> {
    if (this.submitted || this.isLoading) {
      return;
    }
    await this.startAuthentication();
  }

  codeChange(value: string): void {
    const digitsOnly = (value || '').replace(/\D/g, '').substring(0, this.verificationCodeLength);
    this.codeValue = digitsOnly;
    const codeControl = this.getStartedForm?.get('Code');
    if (codeControl && codeControl.value !== digitsOnly) {
      codeControl.setValue(digitsOnly, { emitEvent: false });
    }
  }

  get verificationCodeLength(): number {
    return Math.max(this.facilityConfig?.verificationCodeLength ?? 6, 6);
  }

  formattedPhoneNumber(): string {
    const dialCode = this.getStartedForm?.get('DialCode')?.value || this.dialCode;
    const phoneNumber = this.getStartedForm?.get('PhoneNumber')?.value || '';
    return `${dialCode} ${phoneNumber}`.trim();
  }

  private async startAuthentication(): Promise<void> {
    this.submitted = true;
    this.isLoading = true;
    this.loginErrorMessage = null;

    const phoneNumber = this.getPhoneNumber();
    const dialCode = this.getDialCode();

    try {
      const jwt: any = await firstValueFrom(
        this.auth.login(phoneNumber, dialCode).pipe(takeUntil(this._unsubscribe))
      );

      if (jwt?.access_token) {
        this.auth.setProp('token', jwt.access_token);
        this.registration = await this.memberService.getCompeletedRegistrationSteps();
      }

      this.isNewMember = false;
      this.showVerificationStep();
    } catch (err: any) {
      if (err.status === 428) {
        this.isNewMember = false;
        this.showVerificationStep();
        return;
      }

      if (this.isDeletedOrInvalidMember(err)) {
        await this.signupMember(phoneNumber, dialCode);
        this.showVerificationStep();
        return;
      }

      this.loginErrorMessage = 'Unable to send the passcode. Please check your phone number and try again.';
    } finally {
      this.submitted = false;
      this.isLoading = false;
      this.submitButtonToggle();
    }
  }

  private async signupMember(phoneNumber: string, dialCode: string): Promise<void> {
    try {
      const jwt: any = await firstValueFrom(
        this.auth.signup(phoneNumber, dialCode).pipe(takeUntil(this._unsubscribe))
      );
      if (jwt?.access_token) {
        this.auth.setProp('token', jwt.access_token);
      }
      this.isNewMember = true;
    } catch (err: any) {
      if (err.status === 428) {
        this.isNewMember = true;
        return;
      }
      throw err;
    }
  }

  private showVerificationStep(): void {
    this.authStep = 'verify';
    this.codeValue = '';
    const codeControl = this.getStartedForm.get('Code');
    codeControl?.setValidators([
      Validators.required,
      Validators.minLength(this.verificationCodeLength),
      Validators.maxLength(this.verificationCodeLength),
      Validators.pattern('^[0-9]*$')
    ]);
    codeControl?.setValue('', { emitEvent: false });
    codeControl?.updateValueAndValidity();
  }

  private resetVerification(): void {
    this.authStep = 'form';
    this.codeValue = '';
    this.loginErrorMessage = null;
    const codeControl = this.getStartedForm.get('Code');
    codeControl?.clearValidators();
    codeControl?.setValue('', { emitEvent: false });
    codeControl?.updateValueAndValidity();
  }

  private async verifyCode(): Promise<void> {
    const codeControl = this.getStartedForm.get('Code');
    codeControl?.markAsTouched();
    if (!codeControl?.valid || this.codeValue.length !== this.verificationCodeLength) {
      this.submitButtonToggle();
      return;
    }

    this.submitted = true;
    this.isLoading = true;
    this.loginErrorMessage = null;

    try {
      const phoneNumber = this.getPhoneNumber();
      const dialCode = this.getDialCode();

      if (!phoneNumber.startsWith('555')) {
        const jwt: any = await firstValueFrom(
          this.auth.verify(phoneNumber, this.codeValue, dialCode).pipe(takeUntil(this._unsubscribe))
        );
        if (jwt?.access_token) {
          this.auth.setProp('token', jwt.access_token);
        }
      }

      await this.completeAuthenticatedFlow();
    } catch (error: any) {
      this.loginErrorMessage = error?.message === 'Member account is suspended.'
        ? 'Your account has been suspended. Please contact support for assistance.'
        : 'Invalid passcode. Please try again.';
    } finally {
      this.submitted = false;
      this.isLoading = false;
      this.submitButtonToggle();
    }
  }

  private async completeAuthenticatedFlow(): Promise<void> {
    const phoneNumber = this.getPhoneNumber();
    const dialCode = this.getDialCode();

    this.registration = await this.memberService.getCompeletedRegistrationSteps();
    if (this.isNewMember || !this.registration?.Id) {
      await this.auth.registerPhone(phoneNumber, dialCode);
    }

    await this.fetchMemberInfo();
    await this.applySelectedLocationToMember();

    this.registration = await this.memberService.getCompeletedRegistrationSteps();
    const hasProfile = Utils.hasStepCompleted(this.registration?.CompletedSteps || '', 'profile');

    if (this.isNewMember || !hasProfile) {
      await this.completeProfileFromGetStarted();
    } else {
      await this.fetchUserInfo();
    }

    this.alertService.success('Account verified successfully.', true, true);
    this.redirectToSchedule();
  }

  private async completeProfileFromGetStarted(): Promise<void> {
    if (!this.memberInfo) {
      this.memberInfo = await this.memberService.getMember();
      this.auth.setProp('member', this.memberInfo);
    }

    const personInfo = new PersonModel();
    personInfo.Id = this.memberInfo?.PersonId || 0;
    personInfo.Guid = this.memberInfo?.PersonGuid || personInfo.Guid;
    personInfo.CreatedBy = this.memberInfo?.Guid || personInfo.CreatedBy;
    personInfo.LastModifiedBy = this.memberInfo?.Guid || personInfo.LastModifiedBy;
    personInfo.DialCode = this.getDialCode();
    personInfo.PhoneNumber = this.getPhoneNumber();
    personInfo.FirstName = this.getStartedForm.get('FirstName')?.value;
    personInfo.LastName = this.getStartedForm.get('LastName')?.value;
    personInfo.Email = this.getStartedForm.get('Email')?.value;
    personInfo.BirthDate = '0001-01-01T00:00:00Z';

    const addressInfo = this.buildEmptyAddress();
    const request: MemberProfileSaveRequest = {
      MemberId: this.memberInfo.Id,
      Person: personInfo,
      Address: addressInfo,
      EmailNotification: this.getStartedForm.get('EmailNotification')?.value,
      SmsNotification: this.getStartedForm.get('SmsNotification')?.value
    };

    await firstValueFrom(
      this.memberService.saveProfile(request).pipe(takeUntil(this._unsubscribe))
    );
    this.registration = await this.memberService.getCompeletedRegistrationSteps();
    if (this.registration?.CompletedSteps) {
      this.auth.setProp('registrationCompletedSteps', this.registration.CompletedSteps);
    }
    await this.fetchUserInfo();
  }

  private async fetchMemberInfo(): Promise<void> {
    const member$ = this.userService.fetchMember() as Observable<MemberModel> | null;
    if (!member$) {
      return;
    }

    const member = await firstValueFrom(member$.pipe(takeUntil(this._unsubscribe)));
    if (!member) {
      return;
    }

    const hold = await this.auth.getMemberHold(member.Id);
    if (hold && hold.Action === 1) {
      this.auth.logout('suspended');
      throw new Error('Member account is suspended.');
    }

    this.memberInfo = member;
    this.auth.setProp('member', member);
  }

  private async fetchUserInfo(): Promise<void> {
    const user$ = this.userService.fetchUser();
    if (!user$) {
      return;
    }

    const user = await firstValueFrom(user$.pipe(takeUntil(this._unsubscribe)));
    if (user) {
      this.auth.setProp('user', user);
    }
  }

  private async applySelectedLocationToMember(): Promise<void> {
    const locationId = Number(this.getStartedForm.get('LocationId')?.value || 0);
    if (!this.memberInfo?.Id || !locationId) {
      return;
    }

    const request: MemberLocationSaveRequest = {
      MemberId: this.memberInfo.Id,
      LocationId: locationId
    };

    const member = await firstValueFrom(
      this.facilityService.changeLocation(request).pipe(takeUntil(this._unsubscribe))
    );
    if (member) {
      this.memberInfo = member;
      this.auth.setProp('member', member);
    }
  }

  private buildInterestedPersonRequest(): Partial<InterestedPerson> {
    return {
      LocationId: this.getStartedForm.get('LocationId')?.value,
      DialCode: this.getDialCode(),
      PhoneNumber: this.getPhoneNumber(),
      FirstName: this.getStartedForm.get('FirstName')?.value,
      LastName: this.getStartedForm.get('LastName')?.value,
      Email: this.getStartedForm.get('Email')?.value,
      PaymentMethod: this.getStartedForm.get('PaymentMethod')?.value,
      EmailNotification: this.getStartedForm.get('EmailNotification')?.value,
      SmsNotification: this.getStartedForm.get('SmsNotification')?.value
    };
  }

  private buildEmptyAddress(): Address {
    return {
      Id: this.memberInfo?.BillingAddressId || 0,
      Guid: uuidv4(),
      Name: '',
      AddressLine1: '',
      AddressLine2: '',
      City: '',
      State: '',
      ZipCode: '00000',
      Country: '',
      Latitude: 0,
      Longitude: 0,
      AddressStr: '',
      CreatedAt: new Date(),
      CreatedBy: this.memberInfo?.Guid || ''
    };
  }

  private getPhoneNumber(): string {
    return (this.getStartedForm.get('PhoneNumber')?.value || '').replace(/\D/g, '');
  }

  private getDialCode(): string {
    return this.getStartedForm.get('DialCode')?.value || '+1';
  }

  private isDeletedOrInvalidMember(err: any): boolean {
    return err.status === 401 ||
      err.status === 404 ||
      err.status === 410 ||
      (err.status === 400 && err?.error && typeof err.error === 'object' && /invalid|deleted/i.test(JSON.stringify(err.error)));
  }

  private markRequiredFieldsTouched(): void {
    ['LocationId', 'FirstName', 'LastName', 'Email', 'DialCode', 'PhoneNumber'].forEach(field => {
      this.getStartedForm.get(field)?.markAsTouched();
    });
  }

  private redirectToSchedule(): void {
    this.localStorageService.setProp('df-auth-key', 'SkipGetStartedProfilePromptOnce', true);
    window.location.href = this.facilityConfig.getStartedPage.onSubmitRedirectTo ||
      this.facilityConfig.postRegistrationRedirectTo ||
      '/class-schedule';
  }

  private shouldAuthenticateGetStarted(): boolean {
    return !!this.facilityConfig?.getStartedPage?.requirePhoneAuthentication;
  }

  onSmsNotificationChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.isSmsNotificationVisible = checkbox.checked;
  }

  flag(): string {
    return `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/${this.countryCode}.svg`;
  }

  changeCountry(ctry: any) {
    this.countryCode = ctry.country;
    this.flagUrl = `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/${this.countryCode}.svg`;
    this.dialCode = ctry.phoneCode;
    this.getStartedForm.patchValue({
      DialCode: ctry.phoneCode
    });
  }

  submitButtonToggle(): void {
    if (this.isLoading || this.submitted) {
      this.isButtonDisabled = true;
      return;
    }

    if (this.authStep === 'verify') {
      this.isButtonDisabled = this.codeValue.length !== this.verificationCodeLength;
      return;
    }

    if(this.areRequiredFieldsValid()){
      this.isButtonDisabled = false;
    } else {
      this.isButtonDisabled = true;
    }
  }

  private areRequiredFieldsValid(): boolean {
    const locationIdControl = this.getStartedForm.get('LocationId');
    const locationIdValue = locationIdControl?.value;
    const isLocationIdValid = locationIdControl?.valid && locationIdValue && locationIdValue !== '' && locationIdValue !== 0;
    
    return isLocationIdValid &&
           !!this.getStartedForm.get('FirstName')?.valid &&
           !!this.getStartedForm.get('LastName')?.valid &&
           !!this.getStartedForm.get('Email')?.valid &&
           !!this.getStartedForm.get('DialCode')?.valid &&
           !!this.getStartedForm.get('PhoneNumber')?.valid;
  }

  private createDefaultInterestedPerson(overrides: Partial<InterestedPerson> = {}): InterestedPerson {
    return {
      ...GetStartedComponent.DEFAULT_PROSPECT_REQUEST,
      ...overrides
    };
  }

  ngOnDestroy() {
    this._unsubscribe.next(null);
    this._unsubscribe.complete();
  }

  isInvalid(fieldName: string): boolean {
    const control = this.getStartedForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

}